import { useState, useMemo } from "react";
import { Copy, Check, Code, Network } from "lucide-react";
import JsonView from "react18-json-view";
import "react18-json-view/src/style.css";
import "react18-json-view/src/dark.css";
import Prism from "prismjs";
import "prismjs/components/prism-json";

interface JsonViewerProps {
  data: any;
  title?: string;
  maxHeight?: string;
  className?: string;
  defaultMode?: "tree" | "raw";
  initialCollapsedDepth?: number;
}

export function JsonViewer({
  data,
  title,
  maxHeight = "max-h-[480px]",
  className = "",
  defaultMode = "tree",
  initialCollapsedDepth = 1,
}: JsonViewerProps) {
  const [copied, setCopied] = useState(false);
  const [mode, setMode] = useState<"tree" | "raw">(defaultMode);
  const collapsedRule = useMemo(() => {
    return ({ depth }: { depth: number }) => depth > initialCollapsedDepth;
  }, [initialCollapsedDepth]);

  // Normalize parsed object for tree view and formatted string for raw view
  const parsedData = useMemo(() => {
    if (data === undefined || data === null) return data;
    if (typeof data === "string") {
      try {
        return JSON.parse(data);
      } catch {
        return data;
      }
    }
    return data;
  }, [data]);

  const rawJson = useMemo(() => {
    if (data === undefined) return "undefined";
    if (data === null) return "null";
    if (typeof data === "string") {
      try {
        const parsed = JSON.parse(data);
        return JSON.stringify(parsed, null, 2);
      } catch {
        return data;
      }
    }
    try {
      return JSON.stringify(data, null, 2);
    } catch (e: any) {
      return `/* Failed to serialize JSON: ${e?.message || "Unknown error"} */\n${String(data)}`;
    }
  }, [data]);

  const highlightedHtml = useMemo(() => {
    if (mode !== "raw") return "";
    try {
      if (Prism.languages.json) {
        return Prism.highlight(rawJson, Prism.languages.json, "json");
      }
      return rawJson;
    } catch {
      return rawJson;
    }
  }, [rawJson, mode]);

  const lineCount = useMemo(() => rawJson.split("\n").length, [rawJson]);

  const handleCopy = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(rawJson);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = rawJson;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy JSON:", err);
    }
  };

  const isDarkMode = typeof document !== "undefined" && document.documentElement.classList.contains("dark");

  return (
    <div
      className={`rounded-xl min-w-0 border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 overflow-hidden shadow-2xs transition-colors ${className}`}
    >
      {/* Top Header with title, mode switch, stats, and copy button */}
      <div className="flex items-center justify-between px-3.5 py-2 border-b border-stone-200 dark:border-stone-800 bg-stone-50/75 dark:bg-stone-800/50 transition-colors">
        <div className="flex items-center gap-2 min-w-0">
          {title && (
            <span className="text-[11px] font-bold uppercase tracking-wider text-stone-600 dark:text-stone-300 truncate">
              {title}
            </span>
          )}
          <span className="px-1.5 py-0.5 rounded text-[10px] font-mono font-medium bg-stone-200/70 dark:bg-stone-700/60 text-stone-600 dark:text-stone-400 shrink-0">
            JSON
          </span>
          <span className="text-[10px] font-mono text-stone-400 dark:text-stone-500 shrink-0">
            {lineCount} {lineCount === 1 ? "line" : "lines"}
          </span>
        </div>

        <div className="flex items-center gap-1.5">
          {/* View Mode Toggle: Tree vs Raw */}
          <div className="inline-flex rounded-lg border border-stone-200 dark:border-stone-700 p-0.5 bg-stone-100/80 dark:bg-stone-800/80 text-[10px] font-medium">
            <button
              type="button"
              onClick={() => setMode("tree")}
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md transition ${
                mode === "tree"
                  ? "bg-white dark:bg-stone-700 text-stone-900 dark:text-stone-100 shadow-2xs font-semibold"
                  : "text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200"
              }`}
              title="Interactive tree viewer"
            >
              <Network className="w-3 h-3" />
              <span>Tree</span>
            </button>
            <button
              type="button"
              onClick={() => setMode("raw")}
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md transition ${
                mode === "raw"
                  ? "bg-white dark:bg-stone-700 text-stone-900 dark:text-stone-100 shadow-2xs font-semibold"
                  : "text-stone-500 dark:text-stone-400 hover:text-stone-800 dark:hover:text-stone-200"
              }`}
              title="Formatted raw JSON"
            >
              <Code className="w-3 h-3" />
              <span>Raw</span>
            </button>
          </div>

          {/* Small Copy Icon Button */}
          <button
            type="button"
            onClick={handleCopy}
            aria-label={copied ? "Copied" : "Copy JSON"}
            title={copied ? "Copied!" : "Copy to clipboard"}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-mono transition border ${
              copied
                ? "bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700"
                : "bg-white dark:bg-stone-800 text-stone-500 dark:text-stone-400 hover:text-stone-900 dark:hover:text-stone-100 hover:bg-stone-100 dark:hover:bg-stone-700 border-stone-200 dark:border-stone-700"
            }`}
          >
            {copied ? (
              <>
                <Check className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
                  Copied
                </span>
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5" />
                <span className="text-[10px]">Copy</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Content View: Interactive Tree or Raw */}
      <div className={`relative bg-stone-50/50 dark:bg-stone-950 transition-colors p-3.5 text-xs font-mono overflow-auto ${maxHeight} [scrollbar-gutter:stable]`}>
        {mode === "tree" ? (
          typeof parsedData === "object" && parsedData !== null ? (
            <JsonView
              src={parsedData}
              dark={isDarkMode}
              theme="default"
              enableClipboard={false}
              collapsed={collapsedRule}
              className="!bg-transparent text-stone-800 dark:text-stone-200 text-xs font-mono"
            />
          ) : (
            <div className="text-stone-700 dark:text-stone-300 font-mono">
              {String(parsedData)}
            </div>
          )
        ) : (
          <pre
            className={`text-xs font-mono leading-relaxed text-stone-800 dark:text-stone-200
              [&_.token.property]:text-sky-700 dark:[&_.token.property]:text-sky-300 [&_.token.property]:font-medium
              [&_.token.string]:text-emerald-700 dark:[&_.token.string]:text-emerald-300
              [&_.token.number]:text-amber-700 dark:[&_.token.number]:text-amber-300 [&_.token.number]:font-mono
              [&_.token.boolean]:text-purple-700 dark:[&_.token.boolean]:text-purple-300 [&_.token.boolean]:font-semibold
              [&_.token.null]:text-rose-600 dark:[&_.token.null]:text-rose-400 [&_.token.null]:italic
              [&_.token.punctuation]:text-stone-500 dark:[&_.token.punctuation]:text-stone-400
              [&_.token.operator]:text-stone-500 dark:[&_.token.operator]:text-stone-400
              [&_.token.comment]:text-stone-400 dark:[&_.token.comment]:text-stone-500 [&_.token.comment]:italic`}
            dangerouslySetInnerHTML={{ __html: highlightedHtml }}
          />
        )}
      </div>
    </div>
  );
}
